import { History } from 'history';
import React, {
  FC,
  Fragment,
  MutableRefObject,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import * as OrderSummary from '../../../shared/orders/model';
import { BillingDetails } from '../../../shared/orders/payment';
import { ProductCategoryModel } from '../../../shared/orders/product';
import { PaymentGatewayConfig } from '../../APIClient';
import {
  ActionButton,
  BillingAddress,
  Loading,
  MaterialIcon,
  PageHeader,
  RichMarkdownIt,
  UserStateComponent,
} from '../../components';
import { Currency } from '../../components/Currency';
import { paymentGateways } from '../../components/PaymentGateway';
import { PaymentState, PaymentUpdate } from '../../components/payment';
import { TranslationKey, useTranslation } from '../../translations';
import {
  classNames,
  Fetcher,
  isLogicError,
  isResourceError,
  sumBy,
  useConfig,
  useConvention,
  useFetcher,
  useUserSafe,
} from '../../utils';
import { captureError, LogicError } from '../../utils/errorHandling';
import { OrderItemProduct, RenderOrderSummary, VoucherComponent } from '.';

import './cart.scss';

interface CartProps {
  readonly history?: History;
  readonly onProcessing?: () => void;
  readonly onSuccess?: () => void;
  readonly onError?: () => void;
}

export const Cart: FC<CartProps> = ({ history, onProcessing, onSuccess, onError }) => {
  const { ts } = useTranslation();
  const user = useUserSafe();
  const [failureCount, setFailureCount] = useState(0);
  const [paymentState, setPaymentState] = useState(PaymentState.Pending);
  const [currentOrder, setCurrentOrder] = useState<OrderSummary.Order | undefined>(undefined);
  const [paymentStateMessage, setPaymentStateMessage] = useState<TranslationKey | undefined>(
    undefined,
  );

  const keepCurrentOrder = useRef(false);

  const fetcher = useFetcher(async () => {
    const [categories, order, gateway] = await Promise.all([
      api.getProductCategories(),
      currentOrder && keepCurrentOrder.current
        ? api.getOrderById(currentOrder.id)
        : api.getActiveOrder(user.id),
      api.getPaymentGateways(),
    ]);

    if (currentOrder && keepCurrentOrder.current && order.status === 'paid') {
      updateOrderState({
        state: PaymentState.Success,
        message: 'common.your_payment_has_been_successful',
      });
    }

    setCurrentOrder(order);

    return { categories, order, gateway };
  }, [user]);

  const updateOrderState = useCallback(
    ({
      state: newPaymentState,
      message: newPaymentStateMessage,
    }: {
      state: PaymentState;
      message?: TranslationKey;
    }) => {
      switch (newPaymentState) {
        case PaymentState.Processing: {
          if (onProcessing) {
            onProcessing();
          }

          break;
        }

        case PaymentState.Success: {
          if (onSuccess) {
            onSuccess();
          }

          break;
        }

        case PaymentState.Cancelled:
        case PaymentState.Failed:
        case PaymentState.Declined: {
          if (onError) {
            onError();
          }

          setFailureCount(failureCount + 1);

          break;
        }

        case PaymentState.Pending: {
          break;
        }
      }

      setPaymentState(newPaymentState);
      setPaymentStateMessage(newPaymentStateMessage);
    },
    [failureCount, onError, onProcessing, onSuccess],
  );

  if (!fetcher.success && !isResourceError(fetcher.error, 'Order')) {
    return <Fetcher result={fetcher} />;
  }

  const hasOrder =
    currentOrder && (currentOrder.orderItems.length > 0 || currentOrder.fees.length > 0);

  return (
    <UserStateComponent>
      <PageHeader>{ts('common.checkout')}</PageHeader>
      <Row className="justify-content-center">
        {fetcher.data && hasOrder ? (
          <RenderOrder
            categories={fetcher.data.categories}
            failureCount={failureCount}
            gateway={fetcher.data.gateway}
            keepCurrentOrder={keepCurrentOrder}
            order={fetcher.data.order}
            paymentState={paymentState}
            paymentStateMessage={paymentStateMessage}
            refresh={fetcher.refresh}
            updateOrderState={updateOrderState}
          />
        ) : (
          <EmptyCart history={history} />
        )}
      </Row>
    </UserStateComponent>
  );
};

const EmptyCart: FC<{ readonly history?: History }> = ({ history }) => {
  const { ts } = useTranslation();
  return (
    <Col className="text-center" id="cartEmpty" lg={8} xs={12}>
      <MaterialIcon large name="shopping_cart" />
      <h4>{ts('cart.nothing_in_your_cart_yet')}</h4>
      {history ? (
        <>
          <p>{ts('cart.lets_find_you_an_event')}</p>
          <Row className="justify-content-center">
            <Col lg={5} xs={12}>
              <Button block color="primary" outline tag={Link} to="/event/register">
                {ts('cart.go_to_registrations')}
              </Button>
            </Col>
          </Row>
        </>
      ) : (
        <p>{ts('cart.if_you_were_expecting_something')}</p>
      )}
    </Col>
  );
};

const RenderOrder: FC<{
  readonly categories: ProductCategoryModel[];
  readonly gateway: PaymentGatewayConfig;
  readonly paymentState: PaymentState;
  readonly order: OrderSummary.Order;
  readonly keepCurrentOrder: MutableRefObject<boolean>;
  readonly failureCount: number;
  readonly paymentStateMessage?: TranslationKey;
  readonly refresh: () => void;
  readonly updateOrderState: (update: PaymentUpdate) => void;
}> = ({
  categories,
  paymentState,
  order,
  refresh,
  keepCurrentOrder,
  paymentStateMessage,
  gateway,
  failureCount,
  updateOrderState,
}) => {
  const items = order.orderItems;
  const isUnpaid = paymentState !== PaymentState.Success && order.status !== 'paid';
  const { ts } = useTranslation();
  const user = useUserSafe();

  const defaultBilling: BillingDetails = useMemo(
    () => ({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      addressLine1: user.addressLine1,
      addressLine2: user.addressLine2,
      city: user.addressCity,
      country: user.addressCountry,
      state: user.addressState,
      zipcode: user.addressZipcode,
    }),
    [user],
  );

  const [billingDetails, setBillingDetails] = useState(defaultBilling);

  const updateBillingDetails = useCallback(
    (field: string, value: string | null) => {
      if (value === null && field === 'reset') {
        setBillingDetails(defaultBilling);
        return;
      }

      if (value === null && field !== 'addressLine2') {
        throw new Error('Null value passed for billing detail?');
      }

      setBillingDetails({ ...billingDetails, [field]: value! });
    },
    [billingDetails, defaultBilling],
  );

  return (
    <>
      <Col lg={10} md={12}>
        <Alert color="warning">
          <strong>{ts('common.important')}</strong>
          <br />
          {ts('cart.when_making_payments_please_do')}
        </Alert>
      </Col>
      <Col className="order mb-10" lg={6} md={12}>
        <Card className="mb-10">
          <CardBody>
            <h4>
              {items.length}
              {ts(items.length === 1 ? 'common.item_sing' : 'common.item_plural')}
            </h4>
            <hr />
            {order.orderItems.map((item) => (
              <OrderItemProduct
                item={item}
                key={item.id}
                onUpdate={() => {
                  keepCurrentOrder.current = true;
                  refresh();
                }}
                order={order}
                readOnly={!isUnpaid}
              />
            ))}
            {order.fees.length > 0 && (
              <Fragment key="orderFees">
                <div className="order-line">
                  <div className="order-item">
                    <div className="order-item-description">
                      {ts('common.fees_charges_and_fines')}
                    </div>
                    <div className="order-item-price">
                      <Currency value={sumBy(order.fees, (fee) => fee.amount)} />
                    </div>
                  </div>
                </div>
                <hr />
              </Fragment>
            )}
          </CardBody>
        </Card>
        <BillingAddress
          billingDetails={billingDetails}
          updateBillingDetails={updateBillingDetails}
        />
      </Col>
      <Col lg={6} md={12} xl={4}>
        <Card className="mb-10">
          <CardBody>
            <h4>{ts('common.order_summary')}</h4>
            <hr />
            <RenderOrderSummary
              categories={categories}
              isCustomer
              order={order}
              paymentState={paymentState}
            />
          </CardBody>
        </Card>
        <RenderedPaymentState
          failureCount={failureCount}
          paymentState={paymentState}
          paymentStateMessage={paymentStateMessage}
        />
        {isUnpaid && (
          <PaymentOptions
            billingDetails={billingDetails}
            gateway={gateway}
            keepCurrentOrder={keepCurrentOrder}
            order={order}
            paymentState={paymentState}
            refresh={refresh}
            updateOrderState={updateOrderState}
          />
        )}
      </Col>
      <Col lg={10} md={12}>
        <Card className="mt-10" id="transactionSecurity">
          <CardBody>
            <h3>{ts('cart.transaction_security_header')}</h3>
            <hr />
            <RichMarkdownIt source={ts('cart.transaction_body')} span />
          </CardBody>
        </Card>
      </Col>
    </>
  );
};

const RenderedPaymentState: FC<{
  readonly failureCount: number;
  readonly paymentState: PaymentState;
  readonly paymentStateMessage?: TranslationKey;
}> = ({ failureCount, paymentState, paymentStateMessage }) => {
  const { ts } = useTranslation();
  if ([PaymentState.Processing, PaymentState.Pending].includes(paymentState)) {
    return null;
  }

  const success = paymentState === PaymentState.Success;

  return (
    <Card
      className={classNames(
        {
          'bg-danger': !success,
          'bg-success': success,
        },
        'text-white mb-10 payment-status-card',
      )}
      id={success ? 'paymentComplete' : 'paymentFailed'}
    >
      <CardBody>
        {paymentState === PaymentState.Declined && (
          <p>
            <RichMarkdownIt source={ts('cart.your_payment_was_declined')} />
          </p>
        )}
        {paymentStateMessage && ts(paymentStateMessage)}
        {failureCount >= 3 && (
          <div className="payment-status-repeated-failures">
            <hr />
            <p>
              <small>{ts('cart.if_you_repeatedly_see_this')}</small>
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

const PaymentOptions: FC<{
  readonly gateway: PaymentGatewayConfig;
  readonly paymentState: PaymentState;
  readonly order: OrderSummary.Order;
  readonly keepCurrentOrder: MutableRefObject<boolean>;
  readonly billingDetails: BillingDetails;
  readonly refresh: () => void;
  readonly updateOrderState: (update: PaymentUpdate) => void;
}> = ({
  gateway,
  paymentState,
  order,
  keepCurrentOrder,
  billingDetails,
  refresh,
  updateOrderState,
}) => {
  const { pagSeguroEnabled } = useConfig();
  const { ts } = useTranslation();
  const { breakdown, ...newOrder } = order;
  const total = breakdown.total - breakdown.paid;
  const isFreeOrder = breakdown.subtotal === 0;
  const convention = useConvention();

  const request = useFetcher(async () => {
    if (!convention.forceDealerRegistration) {
      return {
        needsRegistration: false,
      };
    }

    if (order.orderItems.some((t) => t.product.category.isVendor)) {
      try {
        const registration = await api.getUserActiveRegistration(order.userId);

        return {
          needsRegistration: !registration,
        };
      } catch (error) {
        if (
          isLogicError(error, LogicError.RegistrationClosed) ||
          isResourceError(error, 'Registration')
        ) {
          return {
            needsRegistration: true,
          };
        }

        captureError(error as Error);
      }
    }

    return {
      needsRegistration: false,
    };
  }, [order]);

  if (!request.success) {
    return <Fetcher inline result={request} />;
  }

  if (convention.forceDealerRegistration && request.data.needsRegistration) {
    // Show some Error that registration is needed
    return (
      <Card className="mb-10" id="dealerCanNotReg">
        <CardHeader>{ts('cart.convention_registration_needed')}</CardHeader>
        <CardBody>
          <p>{ts('cart.dealer_must_register_for_the_convention')}</p>
        </CardBody>
      </Card>
    );
  }

  if (paymentState === PaymentState.Cancelled) {
    return null;
  }

  if (total === 0) {
    return (
      <Card className="mb-10">
        <CardHeader>
          {ts(isFreeOrder ? 'cart.confirm_order' : 'cart.pay_with_voucher_balance')}
        </CardHeader>
        <CardBody>
          <ActionButton
            action={async () => {
              await api.settleOrder(newOrder.id);
            }}
            block
            color="primary"
            id="confirmVoucherOrder"
            onFailure={captureError}
            onSuccess={() => {
              keepCurrentOrder.current = true;
              refresh();
            }}
          >
            {ts('cart.confirm_order')}
          </ActionButton>
        </CardBody>
      </Card>
    );
  }

  /*
   * This is sad, but it reasliy
   * The credit card payment methods on Brazil is dire. Stripe may delay paymets up to 6 weeks
   * And charge customers additional fees.
   * Here we enable PagSeguro only for Brazilian customers.
   */
  const onBrazil = billingDetails.country === 'BR';
  const gateways: ReactNode[] = [];

  gateways.push(
    <VoucherComponent
      key="voucher"
      onVoucherApplied={() => {
        keepCurrentOrder.current = true;
        refresh();
      }}
      orderId={order.id}
    />,
  );

  if (pagSeguroEnabled && onBrazil) {
    gateways.push(<PagSeguroCheckoutArea key="pagseguro" />);
  }

  if (!pagSeguroEnabled || !onBrazil) {
    gateways.push(
      <PaymentCard
        billingDetails={billingDetails}
        gatewayConfig={gateway}
        paymentState={paymentState}
        total={total}
        updateOrderState={updateOrderState}
      />,
    );
  }

  return gateways;
};

const PagSeguroCheckoutArea: FC = () => {
  const { ts } = useTranslation();
  const [buttonLifecycle, setButtonLifecycle] = useState<'available' | 'busy' | 'error' | 'going'>(
    'available',
  );

  const openPagSeguro = useCallback(async () => {
    setButtonLifecycle('busy');
    try {
      const pscl = await api.createPagSeguroCheckoutLink();
      if (pscl.payLink) {
        window.location.assign(pscl.payLink);
        setButtonLifecycle('going');
      } else {
        setButtonLifecycle('error');
      }
    } catch (error) {
      captureError(error as Error);
      setButtonLifecycle('error');
    }
  }, []);

  return (
    <Card className="mb-10">
      <CardHeader>{ts('payPagSeguro.payWithPagSeguro')}</CardHeader>
      <CardBody>
        <Button
          className="w-100"
          color={
            { available: 'primary', busy: 'secondary', going: 'success', error: 'warning' }[
              buttonLifecycle
            ]
          }
          disabled={['busy', 'going'].includes(buttonLifecycle)}
          id="payPagSeguro"
          onClick={['busy', 'going'].includes(buttonLifecycle) ? () => undefined : openPagSeguro}
        >
          {buttonLifecycle === 'available' && (
            <>
              <MaterialIcon name="shopping_cart" small /> {ts('payPagSeguro.pagSeguroCheckout')}
            </>
          )}
          {buttonLifecycle === 'busy' && (
            <>
              <MaterialIcon name="shopping_cart" small /> {ts('payPagSeguro.creatingCheckout')}
            </>
          )}
          {buttonLifecycle === 'going' && (
            <>
              <MaterialIcon name="shopping_cart" small /> {ts('payPagSeguro.redirecting')}
            </>
          )}
          {buttonLifecycle === 'error' && (
            <>
              <MaterialIcon name="shopping_cart" small /> {ts('payPagSeguro.tryAgain')}
            </>
          )}
        </Button>
      </CardBody>
    </Card>
  );
};

interface PaymentCardProps {
  readonly gatewayConfig?: PaymentGatewayConfig;
  readonly paymentState: PaymentState;
  readonly total: number;
  readonly billingDetails: BillingDetails;
  readonly updateOrderState: (update: PaymentUpdate) => void;
}

const PaymentCard: FC<PaymentCardProps> = (props) => {
  const { updateOrderState, total, paymentState, gatewayConfig, billingDetails } = props;
  const user = useUserSafe();
  const convention = useConvention();

  const { ts } = useTranslation();

  for (const gateway of paymentGateways) {
    if (gateway.type === gatewayConfig?.type) {
      return (
        <Card id="paymentCard">
          <CardHeader>{ts('common.pay_by_credit_debit', gateway.name)}</CardHeader>
          {paymentState === PaymentState.Processing && (
            <Loading>
              <p>{ts('common.processing_payment')}</p>
            </Loading>
          )}
          <CardBody>
            <gateway.lazyComponent
              billingDetails={billingDetails}
              conventionName={convention.longName}
              onUpdate={updateOrderState}
              paymentConfig={gatewayConfig}
              total={total}
              user={user}
            />
          </CardBody>
        </Card>
      );
    }
  }

  return (
    <Card>
      <CardHeader>{ts('cart.credit_card_payment_is_not')}</CardHeader>
      <CardBody>{ts('cart.credit_card_payment_is_not')}</CardBody>
    </Card>
  );
};
